/**
 * FRONT-2410 (screen 3): the Building Session's Configuration. FRONT-2473 made
 * it the one place for every switch, in three sections:
 *  - "While building": what EDDY may use while it builds (web, knowledge,
 *    agents), behind one master switch — formerly checkmarks in the "…" menu.
 *  - "Your MCP servers": the user's own Connections servers for the builder (FRONT-2220).
 *  - "In the app": what THIS app can use, each switch shown with the
 *    workspace's rule — the same Capabilities section the app editor uses, so
 *    the two can never disagree. Only once the project is saved as an app.
 */
import type { ReactNode } from 'react';
import { CapabilitiesSection } from '~/features/apps/capabilities-section';
import { BuilderCapabilities } from './builder-capabilities';
import { BuilderMcpServers } from './builder-mcp-servers';

export type ConfigSession = {
  id: string;
  title?: string;
  webToolsEnabled?: boolean;
  knowledgeToolsEnabled?: boolean;
  agentToolsEnabled?: boolean;
  app?: { slug: string } | null;
};

/**
 * The switches, without a frame: since eddy's 2026-10-26 header pass they sit
 * in the Build window's settings panel (the one cog), not a modal of their own.
 */
export function ConfigurationSections({ session, showBuilder }: { session: ConfigSession; showBuilder: boolean }) {
  const slug = session.app?.slug;
  return (
    <div className="flex flex-col gap-6">
      {showBuilder && (
        <Section title="While building" hint="What EDDY may use while it builds this project. A change applies from its next turn.">
          <BuilderCapabilities session={session} />
        </Section>
      )}
      <Section
        title="Your MCP servers"
        hint="Servers from your Connections EDDY may use while it builds this project. A change applies from its next turn."
      >
        <BuilderMcpServers sessionId={session.id} />
      </Section>
      <Section
        title="In the app"
        hint="This app's switches, within what your workspace allows. You choose what it uses; the builder changes these too, and asks you first."
      >
        {slug ? (
          <CapabilitiesSection slug={slug} />
        ) : (
          <p className="text-xs text-muted-foreground">These appear once the project is saved as an app.</p>
        )}
      </Section>
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <div>
        <h3 className="text-sm font-medium">{title}</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
      </div>
      {children}
    </section>
  );
}
